import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchMythicsData } from './db/dbMythics.js';
import { getCachedMythicsData, setCachedMythicsData } from './db/mythicsCache.js';
import './styles/MythicDetail.css';

const MythicDetail = () => {
    const { rank } = useParams();
    const navigate = useNavigate();
    const [mythic, setMythic] = useState(null);
    const [allRanks, setAllRanks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchMythic = async () => {
            setIsLoading(true);
            let data = getCachedMythicsData();
            if (!data) {
                data = await fetchMythicsData();
                setCachedMythicsData(data);
            }
            // Sort ascending so index 0 = lowest rank.
            // prevRank (left ←) → lower rank number
            // nextRank (right →) → higher rank number
            const sorted = [...(data.nfts || [])].sort((a, b) => a.rank - b.rank);
            setAllRanks(sorted.map(m => m.rank));
            const selectedMythic = data.nfts.find((item) => Number(item.rank) === Number(rank));
            setMythic(selectedMythic);
            setIsLoading(false);
        };

        fetchMythic();
    }, [rank]);

    if (isLoading) return <div className="mythic-loading">Loading...</div>;
    if (!mythic) return (
        <div className="mythic-detail-wrapper">
            <div className="mythic-not-found">
                <p>Mythic not found.</p>
                <Link to="/mythics" className="mythic-back-link">← Back to Mythics</Link>
            </div>
        </div>
    );

    const currentIndex = allRanks.indexOf(mythic.rank);
    // Lower rank number = left (←), higher rank number = right (→)
    const lowerRank = currentIndex > 0 ? allRanks[currentIndex - 1] : null;
    const higherRank = currentIndex < allRanks.length - 1 ? allRanks[currentIndex + 1] : null;

    const uniqueOwners = new Set(mythic.owners.filter(Boolean)).size;

    const filterName = mythic.mythicTrait || mythic.name;
    const traits = encodeURIComponent(JSON.stringify([
        { traitType: "Collection", values: ["Mythics"] },
        { traitType: "Mythic", values: [filterName] },
    ]));
    const openSeaLink = `https://opensea.io/collection/yellow-ducklings?search[sortAscending]=false&search[sortBy]=CREATED_DATE&search[stringTraits][0][name]=Collection&search[stringTraits][0][values][0]=Mythics&traits=${traits}&sortBy=created_date&sortDirection=desc`;

    const handleKey = (e) => {
        if (e.key === 'ArrowLeft' && lowerRank != null) navigate(`/mythics/${lowerRank}`);
        if (e.key === 'ArrowRight' && higherRank != null) navigate(`/mythics/${higherRank}`);
    };

    return (
        <div className="mythic-detail-wrapper">
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div className="mythic-detail-container" onKeyDown={handleKey} tabIndex={0}>
                <div className="mythic-image">
                    <img src={mythic.image_url} alt={mythic.name} />
                </div>
                <div className="mythic-info">
                    <Link to="/mythics" className="mythic-back-link">← Back to Mythics</Link>
                    <h1 className="mythic-title">{mythic.name}</h1>
                    {mythic.description && <p className="mythic-description">{mythic.description}</p>}
                    <p className="mythic-rank">Rank: <strong>{mythic.rank} / 60</strong></p>
                    <p className="mythic-stat">Minted: <strong>{mythic.minted}</strong></p>
                    <p className="mythic-stat">Unique Owners: <strong>{uniqueOwners}</strong></p>
                    <p className="mythic-stat">Floor: <strong>{mythic.floor && mythic.floor !== "-" ? mythic.floor : "—"}</strong></p>
                    <a href={openSeaLink} target="_blank" rel="noopener noreferrer" className="mythic-opensea-btn">
                        View on OpenSea
                    </a>
                    <div className="mythic-nav">
                        {lowerRank != null ? (
                            <Link to={`/mythics/${lowerRank}`} className="mythic-nav-btn">← Rank {lowerRank}</Link>
                        ) : (
                            <span className="mythic-nav-btn mythic-nav-btn-disabled">← Prev</span>
                        )}
                        {higherRank != null ? (
                            <Link to={`/mythics/${higherRank}`} className="mythic-nav-btn">Rank {higherRank} →</Link>
                        ) : (
                            <span className="mythic-nav-btn mythic-nav-btn-disabled">Next →</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MythicDetail;
